import { categorizeError, ErrorType, type AppError } from '../../utils/error-utils';

/**
 * Retry configuration for different types of errors
 */
export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  shouldRetry: (error: unknown) => boolean;
}

/**
 * Helper to safely access properties from unknown error types
 * Returns both the categorized AppError and the original error object
 */
interface ErrorWithHeaders {
  headers?: Record<string, string>;
  code?: string;
}

function analyzeError(error: unknown): { appError: AppError; originalError: ErrorWithHeaders } {
  const appError = categorizeError(error);
  const originalError = error as ErrorWithHeaders;
  return { appError, originalError };
}

/**
 * Default retry configurations for different error scenarios
 */
export const RETRY_CONFIGS: Record<string, RetryConfig> = {
  // Network errors - retry more aggressively
  network: {
    maxRetries: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffFactor: 2,
    shouldRetry: (error) => {
      const appError = categorizeError(error);
      return appError.type === ErrorType.NETWORK;
    },
  },

  // Server errors (5xx) - retry with longer delays
  server: {
    maxRetries: 3,
    baseDelay: 2000,
    maxDelay: 60000,
    backoffFactor: 2.5,
    shouldRetry: (error) => {
      const appError = categorizeError(error);
      return appError.type === ErrorType.SERVER && appError.statusCode !== 429;
    },
  },

  // Rate limiting (429) - respect retry-after header
  rateLimit: {
    maxRetries: 3,
    baseDelay: 5000,
    maxDelay: 120000,
    backoffFactor: 3,
    shouldRetry: (error) => {
      const appError = categorizeError(error);
      return appError.statusCode === 429;
    },
  },

  // Timeout errors - retry with exponential backoff
  timeout: {
    maxRetries: 3,
    baseDelay: 1500,
    maxDelay: 45000,
    backoffFactor: 2,
    shouldRetry: (error) => {
      const { appError, originalError } = analyzeError(error);

      // Check original error code (for axios/fetch errors)
      if (originalError.code === 'ECONNABORTED') {
        return true;
      }

      // Check error messages for timeout
      // Runtime safety: message and details might be undefined

      return (
        appError.message.toLowerCase().includes('timeout') ||
        appError.details?.toLowerCase().includes('timeout') ||
        false
      );
    },
  },

  // Default configuration for other errors
  default: {
    maxRetries: 2,
    baseDelay: 1000,
    maxDelay: 15000,
    backoffFactor: 2,
    shouldRetry: () => true,
  },
};

/**
 * Errors that should never be retried
 */
export const NON_RETRYABLE_ERRORS = new Set([
  ErrorType.AUTHENTICATION,
  ErrorType.AUTHORIZATION,
  ErrorType.VALIDATION,
  ErrorType.NOT_FOUND,
]);

/**
 * Get the appropriate retry configuration for an error
 */
export function getRetryConfig(error: unknown): RetryConfig {
  // Check for specific error types
  for (const [key, config] of Object.entries(RETRY_CONFIGS)) {
    if (key !== 'default' && config.shouldRetry(error)) {
      return config;
    }
  }

  // Return default config - we know it exists
  return RETRY_CONFIGS['default'] as RetryConfig;
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(
  attemptIndex: number,
  config: RetryConfig,
  error?: unknown,
): number {
  if (error) {
    const { appError, originalError } = analyzeError(error);

    // Check for Retry-After header (for rate limiting)
    if (appError.statusCode === 429 && originalError.headers?.['retry-after']) {
      const retryAfter = originalError.headers['retry-after'];
      // If it's a number, it's delay in seconds
      const retryAfterNum = Number(retryAfter);
      if (!isNaN(retryAfterNum)) {
        return retryAfterNum * 1000;
      }
      // If it's a date, calculate delay
      const retryDate = new Date(retryAfter);
      if (!isNaN(retryDate.getTime())) {
        return Math.max(0, retryDate.getTime() - Date.now());
      }
    }
  }

  // Calculate exponential backoff with jitter
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffFactor, attemptIndex);

  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter

  // Apply max delay cap
  return Math.min(exponentialDelay + jitter, config.maxDelay);
}

/**
 * Determine if an error should be retried
 */
export function shouldRetryError(error: unknown, failureCount: number): boolean {
  const { appError } = analyzeError(error);

  // Never retry non-retryable errors
  if (NON_RETRYABLE_ERRORS.has(appError.type)) {
    return false;
  }

  // Don't retry client errors (4xx) except rate limiting
  if (
    appError.statusCode &&
    appError.statusCode >= 400 &&
    appError.statusCode < 500 &&
    appError.statusCode !== 429
  ) {
    return false;
  }

  // Get appropriate retry config
  const config = getRetryConfig(error);

  // Check if we've exceeded max retries for this error type
  return failureCount < config.maxRetries && config.shouldRetry(error);
}

/**
 * Enhanced retry function for React Query
 */
export function createRetryFunction(isQuery: boolean = true) {
  return (failureCount: number, error: unknown): boolean => {
    // Mutations have different retry logic (more conservative)
    if (!isQuery) {
      // Only retry network errors and timeouts for mutations
      const appError = categorizeError(error);
      // Runtime safety: message and details might be undefined

      const isTimeout =
        appError.message.toLowerCase().includes('timeout') ||
        appError.details?.toLowerCase().includes('timeout') ||
        false;

      if (appError.type !== ErrorType.NETWORK && !isTimeout) {
        return false;
      }
    }

    return shouldRetryError(error, failureCount);
  };
}

/**
 * Enhanced retry delay function for React Query
 */
export function createRetryDelayFunction() {
  return (attemptIndex: number, error?: unknown): number => {
    const config = getRetryConfig(error);
    return calculateRetryDelay(attemptIndex, config, error);
  };
}

// Add missing export for backwards compatibility
export const useRetryConfig = getRetryConfig;
